import { FC } from 'react'
import {
	Button,
	Card,
	Col,
	Divider,
	Image,
	Progress,
	Row,
	Spin,
	Typography,
} from 'antd'

import useSingleBroadcast from 'hooks/useSingleBroadcast'
import {
	RiChat4Fill,
	RiCheckDoubleFill,
	RiCheckDoubleLine,
	RiGroupFill,
	RiQuestionFill,
	RiRefreshLine,
	RiWallet3Fill,
} from 'react-icons/ri'
import toPrecisionAmount from 'utils/toPrecisionAmount'
import styles from './styles.module.scss'

const BroadcastStats: FC = () => {
	const { broadcast, insights, loading, refresh } = useSingleBroadcast()

	const broadcastCostCalculated =
		broadcast?.status === 'COMPLETED' && broadcast?.cost_consumed > -1

	const currencySelected = broadcast?.client.currency_selected ?? 'INR'

	const cardsData = [
		{
			icon: <RiGroupFill fill='#f4ca17' size={22} />,
			title: 'Total Contacts',
			stats: broadcast?.contactSelected || 0,
		},
		{
			icon: <RiCheckDoubleFill fill='#707070' size={22} />,
			title: 'Delivered',
			stats: insights?.deliveredCount || 0,
		},
		{
			icon: <RiCheckDoubleLine fill='#389e0d' size={22} />,
			title: 'Read',
			stats: insights?.readCount || 0,
		},
		{
			icon: <RiWallet3Fill fill='#9ECC3B' size={22} />,
			title: `${broadcastCostCalculated ? 'Final' : 'Estimated'} Cost (${
				broadcast?.currency || currencySelected
			})`,
			stats: toPrecisionAmount(
				broadcastCostCalculated ? broadcast.cost_consumed : broadcast?.cost || 0
			),
		},
		{
			icon: <RiChat4Fill fill='#17a2b8' size={22} />,
			title: 'Replies',
			stats: insights?.replies || 0,
		},
		{
			icon: <RiQuestionFill fill='#9D1F5D' size={22} />,
			title: 'Enquiry generated',
			stats: insights?.enquiry || 0,
		},
	]

	const getStatus = (() => {
		switch (broadcast?.status) {
			case 'CANCELED':
				return 'Canceled'
			case 'COMPLETED':
				return 'Completed'
			case 'DRAFT':
				return 'Drafted'
			case 'IN_PROCESS':
				return 'currently in Process'
			case 'SCHEDULED':
				return 'scheduled'
			default:
				return ''
		}
	})()

	const actionText = (() => {
		switch (broadcast?.status) {
			case 'CANCELED':
				return 'Broadcast is canceled.'
			case 'COMPLETED':
				return 'Scroll down to see the broadcast details & stats.'
			case 'IN_PROCESS':
				return 'Broadcast is currently in process.'
			case 'SCHEDULED':
				return 'Broadcast is scheduled.'
			default:
				return ''
		}
	})()

	const completedPercentage = broadcast
		? (100 *
				(broadcast?.totalProcessed ||
					(broadcast?.totalSent || 0) + (broadcast?.totalFailed || 0))) /
		  (broadcast?.contactSelected ?? 0) /
		  100
		: 0

	return (
		<Row gutter={12} align='stretch' className={styles.stats}>
			<Col span={12}>
				<Spin spinning={loading}>
					<Card
						title={
							<div className={styles.metaHeader}>
								<div className={styles.metaHeader__Item}>
									<Image src={broadcast?.client?.logo || ''} width={35} height={35} />
									<Button type='link' href={`/clients/${broadcast?.client._id}`}>
										{broadcast?.client.organizationName}
									</Button>
								</div>
								<h4>
									<span>Type:</span> {broadcast?.isTimeSensitive ? 'Instant' : 'Batch'}
								</h4>
							</div>
						}
						extra={
							<Button
								size='large'
								type='ghost'
								icon={<RiRefreshLine />}
								onClick={refresh}
								loading={loading}
							/>
						}
					>
						<Typography.Title level={4}>{broadcast?.name}</Typography.Title>
						<span>Status: </span> Broadcast is {getStatus} <Divider />
						<Progress percent={completedPercentage * 100} showInfo={false} />
						<div className={styles.metaInfo}>
							<span>
								{broadcast?.totalProcessed ||
									(broadcast?.totalSent || 0) + (broadcast?.totalFailed || 0)}{' '}
								Contacts
							</span>
							<span>{broadcast?.contactSelected} Contacts</span>
						</div>
						<Divider />
						<div className={styles.footer}>
							<span>{actionText} </span>
						</div>
					</Card>
				</Spin>
			</Col>
			<Col span={12}>
				<Spin spinning={loading}>
					<Card className={styles.stats__Cards}>
						{/* Grid styles here */}
						<div className={styles.gridStats}>
							{cardsData.map(cardData => (
								<Card className={styles.stats__Card}>
									{cardData.icon}
									<small>{cardData.title}</small>
									<span>{cardData.stats}</span>
								</Card>
							))}
						</div>
					</Card>
				</Spin>
			</Col>
		</Row>
	)
}

export default BroadcastStats
